import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAppData } from '@/app-data';
import * as api from '@/features/backlog/api';
import * as searchApi from '@/features/search/api';
import { Item, ItemLink } from '@/features/backlog/types';
import { taskTypeMeta } from '@/features/backlog/constants';
import { LINK_TYPES, linkPhrase } from '@/features/backlog/link-types';
import { taskPath } from '@/constants/routes';
import { Popover, menuRow } from '@/components/ui/Popover';
import { Check, ChevronDown, Plus, Search, TaskTypeGlyph, Trash, X } from '@/components/ui/icons';
import { cn } from '@/components/ui/cn';

/** Small link-type selector for the add-link form (outward phrasing, e.g. "blocks", "relates to"). */
function LinkTypePicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const current = LINK_TYPES.find((t) => t.key === value) ?? LINK_TYPES[0];
  return (
    <Popover
      portal
      trigger={({ open, toggle }) => (
        <button
          type="button"
          onClick={toggle}
          aria-haspopup="menu"
          aria-expanded={open}
          className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-line bg-surface-inset px-2.5 text-[12.5px] font-medium text-fg-soft transition-colors hover:bg-layer"
        >
          {current.outward}
          <ChevronDown open={open} className="text-fg-faint" />
        </button>
      )}
    >
      {(close) => (
        <div className="min-w-[180px]">
          {LINK_TYPES.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => { onChange(t.key); close(); }}
              className={cn(menuRow, t.key === value ? 'text-fg' : 'text-fg-soft')}
            >
              <span className="flex-1">{t.outward}</span>
              <span className="flex w-3.5 shrink-0 justify-center text-accent">{t.key === value ? <Check /> : null}</span>
            </button>
          ))}
        </div>
      )}
    </Popover>
  );
}

/**
 * The item detail drawer's "Links" section — JIRA-style typed, directional, cross-board associations.
 * Links are grouped by their display phrase (e.g. all "blocks" together, "is blocked by" together);
 * each row opens the linked item in a NEW TAB. "Add link" picks a type + searches any item (across
 * boards) to link. Deletes/creates call back via onChanged to re-fetch.
 */
export default function ItemLinksSection({ itemId, boardId, links, onChanged }: { itemId: string; boardId?: string; links: ItemLink[]; onChanged: () => void }) {
  const { boards } = useAppData();
  const boardById = useMemo(() => new Map(boards.map((b) => [b.id, b])), [boards]);
  const [adding, setAdding] = useState(false);
  const [type, setType] = useState('relates_to');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Item[]>([]);
  const [busy, setBusy] = useState(false);
  const reqSeq = useRef(0);

  const groups = useMemo(() => {
    const m = new Map<string, ItemLink[]>();
    for (const l of links) {
      const label = linkPhrase(l.type, l.direction);
      const arr = m.get(label) ?? [];
      arr.push(l);
      m.set(label, arr);
    }
    return [...m.entries()];
  }, [links]);

  // Debounced cross-board item search for the picker (excludes the current item + already-linked ones).
  const linkedIds = useMemo(() => new Set(links.map((l) => l.item_id)), [links]);
  useEffect(() => {
    if (!adding || !query.trim()) { setResults([]); return undefined; }
    const t = window.setTimeout(() => {
      const seq = ++reqSeq.current;
      searchApi
        // no sort → backend relevance ranking; prefer_board surfaces THIS board's item for a bare key/number.
        .searchItems({ text: query.trim(), clauses: [], limit: 15, prefer_board: boardId })
        .then((r) => { if (seq === reqSeq.current) setResults(r.data.filter((it) => it.id !== itemId && !linkedIds.has(it.id))); })
        .catch(() => { if (seq === reqSeq.current) setResults([]); });
    }, 180);
    return () => window.clearTimeout(t);
  }, [adding, query, itemId, linkedIds, boardId]);

  const create = useCallback(async (target: Item) => {
    setBusy(true);
    try {
      await api.createLink(itemId, { type, target_item_id: target.id });
      setAdding(false); setQuery(''); setResults([]);
      onChanged();
    } catch {
      /* conflict / validation — leave the form open so the user can adjust */
    } finally {
      setBusy(false);
    }
  }, [itemId, type, onChanged]);

  const remove = useCallback((id: string) => { api.deleteLink(id).catch(() => undefined).then(onChanged); }, [onChanged]);

  return (
    <section data-hl="links" className="mt-5 border-t border-line pt-4">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-[13px] font-semibold text-fg-strong">Links{links.length ? <span className="ml-1.5 text-fg-faint">{links.length}</span> : null}</h3>
        {!adding ? (
          <button type="button" onClick={() => setAdding(true)} className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[12px] font-medium text-accent transition-colors hover:bg-layer">
            <Plus className="h-3.5 w-3.5" /> Add link
          </button>
        ) : null}
      </div>

      {groups.length === 0 && !adding ? (
        <p className="text-[12.5px] text-fg-faint">No links yet. Connect this item to another (even on a different board).</p>
      ) : null}

      <div className="space-y-3">
        {groups.map(([label, ls]) => (
          <div key={label}>
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-fg-ghost">{label}</div>
            <div className="space-y-1">
              {ls.map((l) => {
                const board = boardById.get(l.board_id);
                return (
                  <div key={l.id} className="group flex items-center gap-1 rounded-lg">
                    <a
                      href={board ? taskPath(board.slug, l.key) : undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex min-w-0 flex-1 items-center gap-2 rounded-lg px-1.5 py-1.5 transition-colors hover:bg-layer"
                    >
                      <TaskTypeGlyph type={l.item_type} color={taskTypeMeta(l.item_type).color} />
                      <span className="item-key shrink-0 rounded px-1 py-px tnum text-[10.5px] font-semibold">{l.key}</span>
                      <span className="min-w-0 flex-1 truncate text-[12.5px] text-fg-soft">{l.title || 'Untitled'}</span>
                      {l.child_count > 0 ? (
                        <span className="shrink-0 rounded-full bg-accent/12 px-1.5 py-0.5 text-[10px] font-semibold text-accent" title={`${l.child_count} item${l.child_count > 1 ? 's' : ''} in this epic / sub-tree`}>
                          {l.child_count}
                        </span>
                      ) : null}
                    </a>
                    <button type="button" onClick={() => remove(l.id)} aria-label="Remove link" className="mr-0.5 shrink-0 rounded p-1 text-fg-ghost opacity-0 transition hover:text-danger group-hover:opacity-100 focus-visible:opacity-100">
                      <Trash className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {adding ? (
        <div className="mt-2 rounded-xl border border-line bg-surface-inset p-2">
          <div className="flex items-center gap-2">
            <LinkTypePicker value={type} onChange={setType} />
            <div className="relative flex h-8 min-w-0 flex-1 items-center gap-1.5 rounded-lg border border-line bg-surface-raised px-2 focus-within:border-accent/50">
              <Search className="h-3.5 w-3.5 shrink-0 text-fg-faint" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search an item to link…"
                className="min-w-0 flex-1 bg-transparent text-[12.5px] text-fg placeholder:text-fg-ghost focus:outline-none"
              />
            </div>
            <button type="button" onClick={() => { setAdding(false); setQuery(''); setResults([]); }} aria-label="Cancel" className="shrink-0 rounded p-1 text-fg-faint transition-colors hover:text-fg-soft">
              <X className="h-4 w-4" />
            </button>
          </div>
          {query.trim() ? (
            <div className="thin-scroll mt-1.5 max-h-[240px] overflow-auto">
              {results.length === 0 ? (
                <div className="px-2 py-3 text-[12px] text-fg-faint">No matching items.</div>
              ) : (
                results.map((it) => {
                  const board = boardById.get(it.board_id);
                  return (
                    <a
                      key={it.id}
                      href={board ? taskPath(board.slug, it.key) : undefined}
                      // Plain click LINKS the item; ⌘/Ctrl-click, middle-click or right-click → open it
                      // in a new tab (native <a>) so you can inspect it before linking.
                      onClick={(e) => {
                        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                        e.preventDefault();
                        if (!busy) create(it);
                      }}
                      title="Click to link · ⌘/Ctrl-click (or right-click) to open in a new tab"
                      className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-layer"
                    >
                      <TaskTypeGlyph type={it.type} color={taskTypeMeta(it.type).color} />
                      <span className="item-key shrink-0 rounded px-1 py-px tnum text-[10.5px] font-semibold">{it.key}</span>
                      <span className="min-w-0 flex-1 truncate text-[12.5px] text-fg-soft">{it.title || 'Untitled'}</span>
                    </a>
                  );
                })
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
